/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "../../examples/Tables/DataTable";
import useApi from "../../components/Utils/api";
import toast from "react-hot-toast";
import reportsLineChartData from "../dashboard/data/reportsLineChartData";

const STATUS_COLORS = { SUCCESS: "success", FAILED: "error", PARTIAL: "warning" };

function SettlementLogs({ embedded = false, tabsSlot = null } = {}) {
  const api = useApi();
  const { predefinedRanges } = reportsLineChartData;

  const [rows, setRows] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    return [today, today];
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toISOString().slice(0, 10);
  };

  const loadLogs = useCallback(
    (range, status) => {
      setLoading(true);
      const params = new URLSearchParams();
      if (range) {
        params.append("fromDate", formatDate(range[0]));
        params.append("toDate", formatDate(range[1]));
      }
      if (status) params.append("status", status);

      api
        .get(`settlement-logs?${params.toString()}`)
        .then((res) => setRows(res.data || []))
        .catch(() => toast.error("Failed to load settlement logs"))
        .finally(() => setLoading(false));
    },
    [api]
  );

  useEffect(() => {
    loadLogs(dateRange, statusFilter);
  }, []);

  const openDetail = (id) => {
    api
      .get(`settlement-logs/${id}`)
      .then((res) => {
        setDetailData(res.data);
        setDetailOpen(true);
      })
      .catch(() => toast.error("Failed to load run details"));
  };

  const downloadStatement = (id) => {
    api
      .get(`settlement-logs/${id}/download`, { responseType: "blob" })
      .then((res) => {
        const blob = res.data;
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", `settlement-${id}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        toast.success("Statement downloaded successfully");
      })
      .catch(() => toast.error("Statement file not available for download"));
  };

  const columns = [
    { Header: "Statement Date", accessor: "statementDate", align: "left" },
    { Header: "Triggered At", accessor: "triggeredAt", align: "center" },
    {
      Header: "Run At",
      accessor: "runAt",
      Cell: ({ cell }) => new Date(cell.value).toLocaleString(),
      align: "left",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ cell }) => (
        <Chip
          label={cell.value}
          color={STATUS_COLORS[cell.value] || "default"}
          size="small"
        />
      ),
      align: "center",
    },
    { Header: "Matched", accessor: "txnMatched", align: "center" },
    { Header: "Updated", accessor: "txnUpdated", align: "center" },
    { Header: "Skipped", accessor: "txnSkipped", align: "center" },
    { Header: "Batch ID", accessor: "batchId", align: "left" },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ cell, row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="View Run Log" arrow>
            <span>
              <VisibilityIcon
                style={{ cursor: "pointer", fontSize: 20 }}
                onClick={() => openDetail(cell.value)}
              />
            </span>
          </Tooltip>
          {row.original.hasStatementFile && (
            <Tooltip title="Download Statement ZIP" arrow>
              <span>
                <DownloadIcon
                  style={{ cursor: "pointer", fontSize: 20 }}
                  onClick={() => downloadStatement(cell.value)}
                />
              </span>
            </Tooltip>
          )}
        </MDBox>
      ),
      align: "center",
    },
  ];

  const content = (
    <>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Settlement
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="secondary">
                    {rows.length} Runs
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center" alignItems="center" gap={1}>
                <DateRangePicker
                  value={dateRange}
                  disabledDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(range) => {
                    setDateRange(range);
                    loadLogs(range, statusFilter);
                  }}
                  format="d MMMM yyyy"
                  placeholder="Filter by date range"
                />

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      loadLogs(dateRange, e.target.value);
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, height: "38px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="PARTIAL">Partial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4} />
            </Grid>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
              <DataTable
                table={{ columns, rows }}
                showTotalEntries
                isSorted
                noEndBorder
                canSearch={false}
                entriesPerPage
                entriesPerPagePosition="bottom"
              />
              {loading && (
                <MDTypography variant="body2" textAlign="center" mt={2} mb={2}>
                  Loading...
                </MDTypography>
              )}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* Run detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {detailData && (
          <>
            <DialogTitle>
              Settlement Run — {detailData.statementDate} @ {detailData.triggeredAt}
              <Chip
                label={detailData.status}
                color={STATUS_COLORS[detailData.status] || "default"}
                size="small"
                sx={{ ml: 2 }}
              />
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2} mb={2}>
                {[
                  ["Run At", new Date(detailData.runAt).toLocaleString()],
                  ["Batch ID", detailData.batchId || "—"],
                  ["Matched", detailData.txnMatched ?? "—"],
                  ["Updated", detailData.txnUpdated ?? "—"],
                  ["Skipped", detailData.txnSkipped ?? "—"],
                ].map(([label, value]) => (
                  <Grid item xs={6} sm={4} key={label}>
                    <MDTypography variant="caption" color="text">{label}</MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">{value}</MDTypography>
                  </Grid>
                ))}
              </Grid>

              {detailData.errorMessage && (
                <MDBox mb={2} p={1.5} sx={{ bgcolor: "#fff3f3", borderRadius: 1 }}>
                  <MDTypography variant="caption" color="error" fontWeight="bold">Error</MDTypography>
                  <MDTypography variant="body2" color="error">{detailData.errorMessage}</MDTypography>
                </MDBox>
              )}

              <MDTypography variant="h6" mb={1}>Run Log</MDTypography>
              <MDBox
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.78rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                {detailData.runLog || "No log captured."}
              </MDBox>
            </DialogContent>

            <DialogActions>
              {detailData.hasStatementFile && (
                <MDButton
                  color="info"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadStatement(detailData.id)}
                >
                  Download Statement ZIP
                </MDButton>
              )}
              <MDButton color="secondary" size="small" onClick={() => setDetailOpen(false)}>
                Close
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );

  // embedded=true renders content only (used as a tab inside layouts/settlement)
  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default SettlementLogs;
